import { LAB_SECTIONS, type LabSectionPath } from "@/lib/lab-sections";

export type ErrorPageSurface = "learn" | "lab";

export type DestinationImage = {
  src: string;
  width: number;
  height: number;
};

export type Destination = {
  path: string;
  title: string;
  description: string;
};

export type LabArticleDestination = {
  path: string;
  title: string;
  image: DestinationImage;
};

export function errorPageSurface(pathname: string): ErrorPageSurface {
  return pathname === "/lab" || pathname.startsWith("/lab/") ? "lab" : "learn";
}

export const SURFACE_LEAD: Record<ErrorPageSurface, string> = {
  learn:
    "ゆめさくコードは、図解スライドとAIサポートで、プログラミング初心者が挫折せず学べるオンライン学習サイトです。",
  lab: "ゆめさくエンジニア研究所は、プログラミング・AIの学び方、未経験からのエンジニアキャリア、教材、スクール、技術トレンドを、実体験とデータから検証しています。",
};

const COURSES: Destination = {
  path: "/learn/courses",
  title: "コース一覧",
  description:
    "Python・JavaScript・SQL・生成AIなどのコースを、目的と現在地から選べます。",
};

const LAB: Destination = {
  path: "/lab",
  title: "ゆめさくエンジニア研究所",
  description: "プログラミング・AIの学び方とエンジニアキャリアを検証しています。",
};

const GLOSSARY: Destination = {
  path: "/glossary",
  title: "用語集",
  description: "AI・IT用語を、一言の説明と図解で解説しています。",
};

const TYPING: Destination = {
  path: "/typing",
  title: "タイピング",
  description: "登録なしでブラウザのまま打てる日本語タイピングです。",
};

const CONTACT: Destination = {
  path: "/contact",
  title: "お問い合わせ",
  description: "困ったことや分からないことを受け付けています。",
};

const LAB_TOP: Destination = {
  path: "/lab",
  title: "研究所トップ",
  description: "研究所の記事をまとめて読めます。",
};

function labSection(path: LabSectionPath, title: string): Destination {
  const section = LAB_SECTIONS.find((s) => s.path === path);
  return {
    path: `/lab/${path}`,
    title,
    description: section?.lead ?? "",
  };
}

const NOT_FOUND_DESTINATIONS: Record<ErrorPageSurface, readonly Destination[]> =
  {
    learn: [
      COURSES,
      LAB,
      GLOSSARY,
      TYPING,
      {
        path: "/",
        title: "ホーム",
        description: "目的と現在地に合わせて学べる、トップページです。",
      },
    ],
    lab: [
      LAB_TOP,
      labSection("founder", "代表記事"),
      labSection("voices", "受講生インタビュー"),
      labSection("teachers", "講師インタビュー"),
      { ...COURSES, title: "ゆめさくコードのコース一覧" },
    ],
  };

/*
500 の行き先は、API が落ちていても開ける見込みのあるページに絞る。
タイピングと問い合わせは API を呼ばない。用語集と研究所は KV の控えから出る
*/
const SERVER_ERROR_DESTINATIONS: Record<
  ErrorPageSurface,
  readonly Destination[]
> = {
  learn: [TYPING, CONTACT, GLOSSARY, LAB],
  lab: [LAB_TOP, TYPING, CONTACT],
};

const LAB_ARTICLE_IMAGE_SIZE = { width: 1200, height: 675 } as const;

/*
研究所の記事は API を引かずに持つ。500 の画面は API が落ちているときに出るので、
一覧を取りに行くと、その取得がまた落ちる。絵は public/lab の静的な素材なので API に関係なく出る
*/
export const LAB_ARTICLE_DESTINATIONS: readonly LabArticleDestination[] = [
  {
    path: "/lab/founder/ai-era-engineer-worth-it",
    title: "AI時代に、エンジニアを目指す意味はあるか",
    image: {
      src: "https://yumesaku.tech/lab/ai-era-engineer-worth-it-2026-09.webp",
      ...LAB_ARTICLE_IMAGE_SIZE,
    },
  },
  {
    path: "/lab/founder/engineer-job-types",
    title: "エンジニア職種を徹底解説｜1つのサービスを、だれがどこまで作るのか",
    image: {
      src: "https://yumesaku.tech/lab/engineer-job-types-2026-09.webp",
      ...LAB_ARTICLE_IMAGE_SIZE,
    },
  },
  {
    path: "/lab/founder/study-hours-18-months",
    title:
      "働きながら・子育てしながらプログラミングを学ぶ｜時間のつくり方と、中断から戻る現実",
    image: {
      src: "https://yumesaku.tech/lab/pillar-02-study-reality-2026-09.webp",
      ...LAB_ARTICLE_IMAGE_SIZE,
    },
  },
  {
    path: "/lab/founder/self-taught-engineer-reality",
    title: "9.9割の人にエンジニア独学は無理",
    image: {
      src: "https://yumesaku.tech/lab/self-taught-engineer-reality-2026-09.webp",
      ...LAB_ARTICLE_IMAGE_SIZE,
    },
  },
];

export function notFoundDestinations(pathname: string): readonly Destination[] {
  return NOT_FOUND_DESTINATIONS[errorPageSurface(pathname)];
}

export function serverErrorDestinations(
  pathname: string,
): readonly Destination[] {
  return SERVER_ERROR_DESTINATIONS[errorPageSurface(pathname)];
}
